// Air-raid alerts flash the smallest admin area that contains them — the
// lightning-tracker treatment for "sirens are sounding NOW".
//
// WHAT flashes: live events of kind `air_raid_alert` whose LAST REPORT is
// younger than ALERT_ACTIVE_MS (5 min). A repeat report merges into the same
// event (dedupe), bumps last_report_at, and re-arms the flash — "stops unless
// it happens again" falls out of the storage model, no timer state to keep.
//
// WHERE it flashes: the extractor may scope the alert (attrs.alertLevel —
// "city" / "province" / "country", optional and usually null); otherwise the
// province when the backend stamped one (store._attach_place stamps admin
// containment on every alert), else the country. Geometry comes from
// /api/boundaries/containing — display-simplified, cached per event — and is
// drawn in a client GeoJSON source: a slow red glow (fill, ~1.8 s) under a
// slower flashing outline (~3.6 s). Deliberately not fast: an alert is a
// standing condition, not an impact.
//
// DISMISS: the banner names every active alert with a ✕ (readstate's
// dismissAlert — separate from feed dismissal, so silencing the map never
// hides the report). The banner exists because a flash with no name is noise:
// what is flashing, and how to stop it, must both be readable in words.

import type * as maplibregl from 'maplibre-gl';
import { type EntityDto, get } from './api';
import { dismissAlert, isAlertDismissed, isDismissed } from './readstate';
import { lastReportOf } from './when';

const ALERT_ACTIVE_MS = 5 * 60_000;
const SRC = 'alert-areas';
const RED = '#e11d2e';

interface Area {
  key: string;                    // level:fid — two alerts in one province flash once
  name: string;
  group: string;                  // ISO3
  level: number;
  geometry: GeoJSON.Geometry;
}

interface ContainingDto {
  fid: number; level: number; shapeId: string; group: string; name: string;
  geometry: GeoJSON.Geometry;
}

let mapRef: maplibregl.Map | null = null;
let tracked: EntityDto[] = [];
// null = every level 404ed (open water, unloaded boundaries) — don't retry
// every render; the map dot still shows the event.
const areaByEvent = new Map<string, Area | null>();
const pending = new Set<string>();
let pulseTimer: number | undefined;
let banner: HTMLElement | null = null;

function pointOf(e: EntityDto): [number, number] | null {
  return e.geometry?.type === 'Point'
    ? (e.geometry.coordinates as [number, number]) : null;
}

/** Widest-to-narrowest is the model's call; the fallback ladder is ours.
 *  Default: province when the backend stamped one, else country — "the
 *  smallest available area". "city" tries the district (adm2) first.
 *  Exported for the test that pins this ladder. */
export function levelsFor(e: EntityDto): number[] {
  const lv = String(e.attrs.alertLevel ?? '');
  if (lv === 'country') return [0];
  if (lv === 'province') return [1, 0];
  if (lv === 'city') return [2, 1, 0];
  return e.attrs.admin1 ? [1, 0] : [0];
}

async function resolveArea(e: EntityDto): Promise<Area | null> {
  const pt = pointOf(e);
  if (!pt) return null;
  for (const level of levelsFor(e)) {
    try {
      const dto = await get<ContainingDto>(
        `/api/boundaries/containing?lon=${pt[0]}&lat=${pt[1]}&level=${level}`);
      return { key: `${level}:${dto.fid}`, name: dto.name, group: dto.group,
               level, geometry: dto.geometry };
    } catch { /* nothing contains at this level — try the wider one */ }
  }
  return null;
}

function activeAlerts(): EntityDto[] {
  const now = Date.now();
  return tracked.filter((e) => !isAlertDismissed(e.id) && !isDismissed(e)
    && now - lastReportOf(e) < ALERT_ACTIVE_MS);
}

function ensureLayers(map: maplibregl.Map): void {
  if (map.getSource(SRC)) return;
  map.addSource(SRC, { type: 'geojson',
                       data: { type: 'FeatureCollection', features: [] } });
  // Under the entity layers: the glow is context, and it must never sit on
  // top of the units and events it is context FOR.
  const before = map.getLayer('entities-fill') ? 'entities-fill' : undefined;
  map.addLayer({
    id: 'alert-fill', type: 'fill', source: SRC,
    paint: { 'fill-color': RED, 'fill-opacity': 0.1 },
  }, before);
  map.addLayer({
    id: 'alert-line', type: 'line', source: SRC,
    paint: { 'line-color': RED, 'line-width': 1.8, 'line-opacity': 0.4 },
  }, before);
}

function tickPulse(): void {
  const map = mapRef;
  if (!map || !map.getLayer('alert-fill')) return;
  const t = performance.now();
  const wave = (period: number) => 0.5 + 0.5 * Math.sin((2 * Math.PI * t) / period);
  map.setPaintProperty('alert-fill', 'fill-opacity', 0.07 + 0.09 * wave(1800));
  map.setPaintProperty('alert-line', 'line-opacity', 0.2 + 0.6 * wave(3600));
}

function bannerRow(e: EntityDto, area: Area | null): HTMLElement {
  const row = document.createElement('div');
  row.className = 'alert-chip';
  const label = document.createElement('span');
  const where = area
    ? (area.level === 0 ? area.name
       : `${area.name}, ${String(e.attrs.admin0 ?? area.group)}`)
    : String(e.attrs.admin1 ?? e.attrs.admin0 ?? e.attrs.place ?? 'unknown area');
  label.textContent = `🚨 Air-raid alert — ${where}`;
  label.title = String(e.attrs.summary ?? '');
  const x = document.createElement('button');
  x.type = 'button';
  x.className = 'x';
  x.textContent = '✕';
  x.title = 'Dismiss this alert — stops the area flash. The report stays in the feed.';
  x.addEventListener('click', () => { dismissAlert(e.id); paint(); });
  row.append(label, x);
  return row;
}

function paint(): void {
  const map = mapRef;
  if (!map) return;
  const active = activeAlerts();

  // Kick off any missing area fetches; each repaints on arrival.
  for (const e of active) {
    if (!areaByEvent.has(e.id) && !pending.has(e.id)) {
      pending.add(e.id);
      void resolveArea(e).then((a) => {
        pending.delete(e.id);
        areaByEvent.set(e.id, a);
        paint();
      });
    }
  }

  const resolved = active
    .map((e) => ({ e, area: areaByEvent.get(e.id) ?? null }))
    .filter((x): x is { e: EntityDto; area: Area } => x.area !== null);

  // The polygons, deduped by area — two alerts in one province flash once.
  const byArea = new Map<string, Area>();
  for (const { area } of resolved) byArea.set(area.key, area);

  if (byArea.size || map.getSource(SRC)) {
    ensureLayers(map);
    (map.getSource(SRC) as maplibregl.GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: [...byArea.values()].map((a) => ({
        type: 'Feature' as const, geometry: a.geometry as never,
        properties: { name: a.name },
      })),
    });
  }

  // The banner: one chip per ALERT (not per area) so each is dismissible on
  // its own even when two share a province.
  if (!banner) {
    banner = document.createElement('div');
    banner.className = 'alert-banner';
    map.getContainer().appendChild(banner);
  }
  banner.replaceChildren(...active.map((e) =>
    bannerRow(e, areaByEvent.get(e.id) ?? null)));
  banner.hidden = active.length === 0;

  // The pulse runs only while something is flashing.
  if (active.length && pulseTimer === undefined) {
    pulseTimer = window.setInterval(() => {
      tickPulse();
      // Cheap expiry check rides the pulse: when the last alert ages out the
      // next paint clears the source and stops this timer.
      if (!activeAlerts().length) paint();
    }, 120);
  } else if (!active.length && pulseTimer !== undefined) {
    window.clearInterval(pulseTimer);
    pulseTimer = undefined;
  }
}

/** Called wherever the live dots are re-baked (objects.setLiveEvents) — one
 *  hook, so alerts can never drift from what the map shows. */
export function updateAlerts(map: maplibregl.Map, events: EntityDto[]): void {
  mapRef = map;
  tracked = events.filter((e) =>
    String(e.attrs.kind ?? '') === 'air_raid_alert' && pointOf(e) !== null);
  paint();
}
